/**
 * Mobile App Messaging Utilities
 *
 * Direct message posting to mobile apps without Redux.
 * Supports Flutter, Android WebView, and iOS WKWebView.
 */

/**
 * Message keys for communication with mobile apps.
 * Mirrors the keys from common/message_to_app_handler.
 */
const messageToAppKeys = {
  action: 'action',
  params: 'params',
  route: 'route',
  title: 'title',
  initialLoaded: 'initial_loaded',
  initialOptionPicked: 'initial_option_picked',
  initiateSession: 'initiate_session',
  skipAndContinue: 'skip_and_continue',
  onPay: 'on_pay',
  onPayCompeted: 'on_pay_completed',
  dismiss: 'dismiss',
  dismissOptions: 'dismiss_options',
  disableDismiss: 'disable_dismiss',
  navigationUpdate: 'navigation_update',
  crossPlatformNavigation: 'cross_platform_navigation',
  nativeBackPressed: 'native_back_pressed',
  isBackAvailable: 'is_back_available',
  paymentMethodAdded: 'payment_method_added',
  analytics: 'analytics',
  okMessage: 'ok_message',
  cashback: 'cashback',
  /** Incoming message from native app: exchange rate changed, close the flow */
  rateChanged: 'rate_changed'
} as const;

/**
 * Detect if running inside a mobile app WebView.
 * Mobile apps include pg_client or client_platform in URL params.
 */
const fromMobileApp = (() => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return !!(params.get('pg_client') || params.get('client_platform'));
})();

// ============================================================================
// Types
// ============================================================================

export interface MobileMessage {
  [key: string]: any;
}

// ============================================================================
// Message Posting
// ============================================================================

/**
 * Post a message directly to the mobile app
 * Supports: Flutter InAppWebView, Android WebView, iOS WKWebView
 */
export const postMessageToApp = (message: MobileMessage): void => {
  if (!fromMobileApp) return;

  const messageStr = JSON.stringify(message);

  // Flutter InAppWebView
  if ((window as any).flutter_inappwebview) {
    (window as any).flutter_inappwebview.callHandler('bargesMessageHandler', messageStr);
  }

  // Android WebView
  if ((window as any).bargesMessageHandler) {
    (window as any).bargesMessageHandler.postMessage(messageStr);
  }

  // iOS WKWebView
  if ((window as any).webkit?.messageHandlers?.bargesMessageHandler) {
    (window as any).webkit.messageHandlers.bargesMessageHandler.postMessage(messageStr);
  }
};

// ============================================================================
// Message Builders
// ============================================================================

/**
 * Build a standard mobile message with action and optional params
 */
export const buildMessage = (
  action: string,
  params?: Record<string, any>
): MobileMessage => ({
  [messageToAppKeys.action]: action,
  ...(params ? { [messageToAppKeys.params]: params } : {})
});

/**
 * Send initial_loaded message
 */
export const sendInitialLoaded = (): void => {
  postMessageToApp(buildMessage(messageToAppKeys.initialLoaded));
};

/**
 * Send on_pay_completed message (simple, no result data)
 */
export const sendPayCompleted = (): void => {
  postMessageToApp(buildMessage(messageToAppKeys.onPayCompeted));
};

/**
 * Send on_pay_completed message with result data (okMessage, cashback, analytics).
 * Matches the old flow's pattern from google_pay and process_apple_pay.
 *
 * @param params.okMessage - Success message from API
 * @param params.cashbackAmount - Cashback earned (string, e.g. "10.50")
 * @param params.analyticEvents - Optional analytics events to forward to native app
 */
export const sendPayCompletedWithResult = (params: {
  okMessage?: string;
  cashbackAmount?: string;
  analyticEvents?: Record<string, Record<string, any>>;
}): void => {
  postMessageToApp(buildMessage(messageToAppKeys.onPayCompeted, {
    [messageToAppKeys.okMessage]: params.okMessage || null,
    [messageToAppKeys.cashback]: params.cashbackAmount
      ? parseFloat(params.cashbackAmount).toFixed(2)
      : null,
    ...(params.analyticEvents ? { analytic_events: params.analyticEvents } : {})
  }));
};

/**
 * Send dismiss_options message to enable/disable native dismiss controls.
 * When processing, the native app should prevent the user from dismissing.
 *
 * @param disable - true to disable dismiss, false to re-enable
 */
export const sendDismissOptions = (disable: boolean): void => {
  postMessageToApp(buildMessage(messageToAppKeys.dismissOptions, {
    [messageToAppKeys.disableDismiss]: disable
  }));
};

/**
 * Send initial_option_picked message.
 * Notifies the native app that the user has started a purchase flow action.
 */
export const sendInitialOptionPicked = (): void => {
  postMessageToApp(buildMessage(messageToAppKeys.initialOptionPicked));
};

/**
 * Send skip_and_continue message.
 * Used when user dismisses an additionalFunds flow to continue without paying.
 */
export const sendSkipAndContinue = (): void => {
  postMessageToApp(buildMessage(messageToAppKeys.skipAndContinue));
};

/**
 * Send initiate_session message.
 * Notifies the native app that a PAYG validation session is starting.
 */
export const sendInitiateSession = (): void => {
  postMessageToApp(buildMessage(messageToAppKeys.initiateSession));
};

/**
 * Send payment_method_added message.
 * Notifies the native app that a new payment method was added.
 * Matches old flow behavior from add_credit_card.js.
 *
 * @param paymentMethodsCount - Total number of payment methods after addition
 */
export const sendPaymentMethodAdded = (paymentMethodsCount: number): void => {
  postMessageToApp(buildMessage(messageToAppKeys.paymentMethodAdded, {
    payment_methods_count: paymentMethodsCount
  }));
};

/**
 * Send on_pay message — delegates payment to the native app (used for IAP).
 * The native app handles the purchase via Apple/Google IAP SDKs.
 *
 * @param params - Payment option data including kind, purchaseFees, etc.
 */
export const sendOnPay = (params: Record<string, any>): void => {
  postMessageToApp(buildMessage(messageToAppKeys.onPay, params));
};

/**
 * Send dismiss message
 */
export const sendDismiss = (): void => {
  postMessageToApp(buildMessage(messageToAppKeys.dismiss));
};

/**
 * Send navigation_update message
 */
export const sendNavigationUpdate = (route: string): void => {
  postMessageToApp(buildMessage(messageToAppKeys.navigationUpdate, {
    [messageToAppKeys.route]: route
  }));
};

/**
 * Send cross_platform_navigation message to the native app.
 * The native app uses this to update its toolbar (title, back button state).
 *
 * Callers are responsible for platform/theme gating — e.g. MobilePurchaseFlow
 * only calls this on Android (matching old PaymentMethodsListTitle behaviour
 * which skipped it for the purchase theme on iOS).
 *
 * @param title - Current screen title text
 * @param isBackAvailable - Whether a back action is available on the current screen
 */
export const sendCrossPlatformNavigation = (title: string, isBackAvailable: boolean): void => {
  postMessageToApp(buildMessage(messageToAppKeys.crossPlatformNavigation, {
    [messageToAppKeys.title]: title,
    [messageToAppKeys.isBackAvailable]: isBackAvailable
  }));
};

/**
 * Send analytics message to mobile app
 * Mobile app will track these events natively
 *
 * @param analyticEvents - Object with event names as keys and properties as values
 * @example
 * sendAnalytics({
 *   'purchase failed': { 'failed reason': 'api error', ... }
 * });
 */
export const sendAnalytics = (analyticEvents: Record<string, Record<string, any>>): void => {
  postMessageToApp(buildMessage(messageToAppKeys.analytics, {
    analytic_events: analyticEvents
  }));
};

// ============================================================================
// Re-export keys for convenience
// ============================================================================

export { messageToAppKeys };
